/**
 * JSON-LD (schema.org) builders for every public page surface.
 * Always returns plain JSON-serializable objects.
 */

const SITE_URL = (typeof window !== 'undefined' && window.location?.origin) || 'https://eventism.com';
const BRAND_NAME = 'Eventism';
const BRAND_LOGO = `${SITE_URL}/eventism-logo.png`;

// US-city → IANA timezone map so Event JSON-LD startDate values include the
// correct UTC offset (Google warns when timezone is missing → carousel risk).
const CITY_TIMEZONES = {
  denver: 'America/Denver',
  las_vegas: 'America/Los_Angeles',
  'las-vegas': 'America/Los_Angeles',
  los_angeles: 'America/Los_Angeles',
  'los-angeles': 'America/Los_Angeles',
  charlotte: 'America/New_York',
  new_york: 'America/New_York',
  'new-york': 'America/New_York',
};

// Map our internal `category` taxonomy to the most-specific schema.org Event
// subtype that Google recognises. Specific subtypes earn better carousel
// placement than the generic `Event` type because Google can match them
// against intent ("comedy shows near me" → ComedyEvent, "festivals
// this weekend" → Festival).
// See: https://schema.org/Event#subtypes
const CATEGORY_TO_EVENT_TYPE = {
  festival: 'Festival',
  concert: 'MusicEvent',
  music: 'MusicEvent',
  live_music: 'MusicEvent',
  nightlife: 'SocialEvent',
  club: 'DanceEvent',
  date_night: 'SocialEvent',
  comedy: 'ComedyEvent',
  theater: 'TheaterEvent',
  theatre: 'TheaterEvent',
  performing_arts: 'TheaterEvent',
  dance: 'DanceEvent',
  brunch: 'FoodEvent',
  tasting: 'FoodEvent',
  food: 'FoodEvent',
  wine: 'FoodEvent',
  workshop: 'EducationEvent',
  class: 'EducationEvent',
  wellness: 'SocialEvent',
  fitness: 'SportsEvent',
  tour: 'SocialEvent',
  networking: 'BusinessEvent',
  business: 'BusinessEvent',
  conference: 'BusinessEvent',
  competition: 'SportsEvent',
  sports: 'SportsEvent',
  party: 'SocialEvent',
  gala: 'SocialEvent',
  fundraiser: 'SocialEvent',
  charity: 'SocialEvent',
  halloween: 'SocialEvent',
  nye: 'SocialEvent',
  exhibition: 'ExhibitionEvent',
  popup: 'ExhibitionEvent',
  art: 'VisualArtsEvent',
  family: 'ChildrensEvent',
  literary: 'LiteraryEvent',
  screening: 'ScreeningEvent',
  film: 'ScreeningEvent',
  // Holiday-themed events default to SocialEvent unless the event also
  // carries a more specific category/tag (e.g. a holiday concert
  // → MusicEvent wins through tag precedence).
  holiday: 'SocialEvent',
  other: 'Event',
};

/**
 * Resolve the most specific schema.org @type for an event. Inspects
 * `event_type`/`category`/`tags` in that order. Falls back to plain `Event`.
 */
export function resolveEventType(event) {
  if (!event) return 'Event';
  const candidates = [
    event.schema_type,           // explicit override wins
    event.event_type,
    event.category,
    ...(event.tags || []),
  ].filter(Boolean).map((c) => String(c).toLowerCase().replace(/\s+/g, '_'));
  for (const c of candidates) {
    if (CATEGORY_TO_EVENT_TYPE[c]) return CATEGORY_TO_EVENT_TYPE[c];
  }
  return 'Event';
}

/**
 * Build the `image` array per Google's recommendation — multiple high-res
 * images (ideally 1:1, 4:3, 16:9). We use the event's gallery if present,
 * else fall back to the primary image_url. Returns an array (Google
 * prefers arrays over single strings for richer carousel cards).
 */
export function buildImageArray(event) {
  if (!event) return [BRAND_LOGO];
  const candidates = [
    event.image_url,
    event.hero_image_url,
    ...(event.gallery_images || []),
  ].filter(Boolean);
  // Dedupe while preserving order
  const seen = new Set();
  const unique = candidates.filter((u) => {
    if (seen.has(u)) return false;
    seen.add(u);
    return true;
  });
  return unique.length > 0 ? unique : [BRAND_LOGO];
}

/**
 * Build the `offers` array — one Offer per ticket type so Google can
 * advertise the price RANGE (e.g. "$45 – $250") in the carousel.
 * Returns an array. Falls back to a single Offer based on
 * `ticket_price_min` when ticket_types is empty.
 */
export function buildOffersArray(event, eventUrl) {
  if (!event) return undefined;
  const tickets = event.ticket_types || event.tickets_breakdown || [];
  const validFrom = new Date().toISOString();
  if (tickets.length > 0) {
    return tickets
      .map((t) => {
        const price = Number(
          t.price_cents != null ? t.price_cents / 100 :
          t.price != null ? t.price : 0
        );
        if (Number.isNaN(price) || price < 0) return null;
        const offer = {
          '@type': 'Offer',
          name: t.name || t.ticket_type_name || 'General Admission',
          price: price.toFixed(2),
          priceCurrency: 'USD',
          availability: t.quantity_total && t.quantity_sold >= t.quantity_total
            ? 'https://schema.org/SoldOut'
            : 'https://schema.org/InStock',
          url: eventUrl,
          validFrom,
        };
        if (t.sale_start) offer.validFrom = t.sale_start;
        if (t.sale_end) offer.validThrough = t.sale_end;
        return offer;
      })
      .filter(Boolean);
  }
  const min = Number(event.ticket_price_min || 0);
  if (min > 0) {
    return [{
      '@type': 'Offer',
      price: min.toFixed(2),
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: eventUrl,
      validFrom,
    }];
  }
  if (event.is_free) {
    return [{
      '@type': 'Offer',
      price: '0',
      priceCurrency: 'USD',
      availability: 'https://schema.org/InStock',
      url: eventUrl,
      validFrom,
    }];
  }
  return undefined;
}

/**
 * Convert "YYYY-MM-DD" + "HH:mm" into an ISO 8601 datetime WITH the right
 * UTC offset for the event's city. Falls back to America/Denver if unknown.
 * Returns undefined if date is missing.
 */
export function addDaysISO(date, days) {
  if (!date) return date;
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  d.setDate(d.getDate() + days);
  const p = (n) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function isoWithTimezone(date, time, city) {
  if (!date) return undefined;
  const tz = CITY_TIMEZONES[(city || '').toLowerCase().replace(/\s+/g, '_')] || 'America/Denver';
  const hhmm = (time || '20:00').slice(0, 5);
  // Build a Date at the wall-clock time in tz, then read its UTC offset.
  try {
    const local = new Date(`${date}T${hhmm}:00`);
    // Use Intl to derive the offset for the city's timezone on this date.
    const dtf = new Intl.DateTimeFormat('en-US', {
      timeZone: tz,
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
      hour12: false, timeZoneName: 'shortOffset',
    });
    const parts = dtf.formatToParts(local);
    const offPart = parts.find((p) => p.type === 'timeZoneName')?.value || 'GMT-07:00';
    // shortOffset looks like "GMT-7" or "GMT-07:00" — normalise to ±HH:MM.
    const m = offPart.match(/GMT([+-])(\d{1,2})(?::?(\d{2}))?/);
    let off = '-07:00';
    if (m) {
      const sign = m[1];
      const hours = m[2].padStart(2, '0');
      const mins = (m[3] || '00').padStart(2, '0');
      off = `${sign}${hours}:${mins}`;
    }
    return `${date}T${hhmm}:00${off}`;
  } catch {
    return `${date}T${hhmm}:00-07:00`;
  }
}

/* ============= ORGANIZATION + SITE (homepage) ============= */
export function organizationLD() {
  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': `${SITE_URL}#organization`,
    name: BRAND_NAME,
    url: SITE_URL,
    logo: BRAND_LOGO,
    description:
      'Eventism is the all-in-one events platform — book caterers, DJs, venues, staff, and full event production for parties, weddings, and corporate events.',
    sameAs: [
      'https://www.instagram.com/eventism',
      'https://www.facebook.com/eventism',
      'https://www.linkedin.com/company/eventism',
      'https://www.tiktok.com/@eventism',
    ],
    areaServed: [
      { '@type': 'City', name: 'Denver' },
      { '@type': 'City', name: 'Las Vegas' },
      { '@type': 'City', name: 'Los Angeles' },
      { '@type': 'City', name: 'Charlotte' },
    ],
  };
}

/**
 * Public Organization profile JSON-LD — used on /organizations/{slug}.
 * Renders as a schema.org Organization with logo, sameAs links, area
 * served, and an AggregateRating when we have one. Specific to one
 * tenant org (NOT the Eventism brand itself).
 */
export function orgProfileLD(org) {
  if (!org) return null;
  const url = `${SITE_URL}/organizations/${org.slug}`;
  const sameAs = [
    org.website,
    org.instagram && `https://instagram.com/${String(org.instagram).replace('@', '')}`,
    org.tiktok && `https://tiktok.com/@${String(org.tiktok).replace('@', '')}`,
    org.facebook,
    org.twitter && `https://twitter.com/${String(org.twitter).replace('@', '')}`,
    org.youtube,
  ].filter(Boolean);
  const ld = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    '@id': url,
    name: org.display_name || org.legal_name,
    legalName: org.legal_name || undefined,
    url,
    description: org.bio || undefined,
    logo: org.logo_url || undefined,
    image: org.cover_url || org.logo_url || undefined,
    foundingDate: org.founded_year ? String(org.founded_year) : undefined,
    sameAs: sameAs.length ? sameAs : undefined,
    areaServed: (org.cities_served || []).map((c) => ({
      '@type': 'City',
      name: String(c).replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()),
    })),
    parentOrganization: { '@id': `${SITE_URL}#organization` },
  };
  if (org.avg_rating != null && org.review_count) {
    ld.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: Number(org.avg_rating),
      reviewCount: Number(org.review_count),
      bestRating: 5,
      worstRating: 1,
    };
  }
  // Strip undefined to keep payloads small
  Object.keys(ld).forEach((k) => ld[k] === undefined && delete ld[k]);
  return ld;
}

export function websiteLD() {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    '@id': `${SITE_URL}#website`,
    name: BRAND_NAME,
    url: SITE_URL,
    publisher: { '@id': `${SITE_URL}#organization` },
    potentialAction: {
      '@type': 'SearchAction',
      target: `${SITE_URL}/services?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
}

/* ============= BREADCRUMBS ============= */
/**
 * items: [{ name: 'Home', url: '/' }, { name: 'Las Vegas', url: '/las_vegas' }, ...]
 */
export function breadcrumbLD(items) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: (items || []).map((it, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: it.name,
      item: it.url?.startsWith('http') ? it.url : `${SITE_URL}${it.url || ''}`,
    })),
  };
}

/* ============= EVENT (event detail page) ============= */
export function eventLD(event) {
  if (!event) return null;
  const startISO =
    event.start_datetime ||
    event.occurrences?.[0]?.start_datetime ||
    isoWithTimezone(event.event_date, event.event_time || event.start_time, event.city);
  // End-past-midnight rollover: a 2:00 AM end on a 8:00 PM show belongs to
  // the NEXT calendar day, otherwise endDate lands before startDate.
  const startTime = event.event_time || event.start_time || '';
  const rollsOver = Boolean(
    event.end_time && startTime && event.end_time.slice(0, 5) <= startTime.slice(0, 5)
  );
  const endDateStr = rollsOver ? addDaysISO(event.event_date, 1) : event.event_date;
  const endISO =
    event.end_datetime ||
    event.occurrences?.[0]?.end_datetime ||
    (event.end_time ? isoWithTimezone(endDateStr, event.end_time, event.city) : undefined);
  // Prefer the structured venue fields the Venue tab writes; the legacy
  // `venue` string is often a stale "TBA" left by old importers.
  const legacy = typeof event.venue === 'object'
    ? event.venue
    : { name: event.venue, address: event.address };
  const venueObj = (event.venue_name && String(event.venue_name).trim())
    ? {
      name: event.venue_name,
      address: event.venue_address,
      city: event.venue_city || event.city,
      state: event.venue_state,
      zip: event.venue_zip,
    }
    : legacy;
  const eventUrl = typeof window !== 'undefined' ? window.location.href : `${SITE_URL}/events`;

  const data = {
    '@context': 'https://schema.org',
    '@type': resolveEventType(event),
    name: event.title || event.name,
    description: (event.description || event.short_description || '').slice(0, 5000),
    image: buildImageArray(event),
    startDate: startISO,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
    organizer: {
      '@type': 'Organization',
      name: BRAND_NAME,
      url: SITE_URL,
    },
  };
  if (endISO) data.endDate = endISO;
  if (venueObj?.name) {
    data.location = {
      '@type': 'Place',
      name: venueObj.name,
      address: {
        '@type': 'PostalAddress',
        streetAddress: venueObj.address || venueObj.street || '',
        addressLocality: venueObj.city || event.city || '',
        addressRegion: venueObj.state || event.state || '',
        postalCode: venueObj.zip || venueObj.postal_code || '',
        addressCountry: 'US',
      },
    };
  }
  // Performer (concerts / festivals): pulls from event.performers[] or a
  // singular event.performer string. Google's MusicEvent rich result
  // requires it.
  const performers = event.performers || (event.performer ? [event.performer] : []);
  if (performers.length > 0) {
    data.performer = performers.map((p) => (
      typeof p === 'object' && p !== null
        ? {
            '@type': p.type || 'PerformingGroup',
            name: p.name,
            ...(p.bio ? { description: p.bio } : {}),
            ...(p.images && p.images.length ? { image: p.images[0] } : {}),
            ...(p.instagram ? { sameAs: [p.instagram] } : {}),
          }
        : { '@type': 'PerformingGroup', name: String(p) }
    ));
  }
  const offers = buildOffersArray(event, eventUrl);
  if (offers && offers.length > 0) {
    // For multiple tiers Google prefers an array (sometimes also accepts
    // AggregateOffer). Array also keeps each tier individually surfaceable.
    data.offers = offers.length === 1 ? offers[0] : offers;
  }
  return data;
}

/* ============= SERVICE / PRODUCT (service detail page) ============= */
export function serviceLD(product, cityDisplay) {
  if (!product) return null;
  const url = typeof window !== 'undefined' ? window.location.href : `${SITE_URL}`;
  const price =
    typeof product.customer_price === 'number' && product.customer_price > 0
      ? product.customer_price
      : Number(product.base_price) || 0;

  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    serviceType: product.category,
    name: product.name,
    description: (product.short_description || product.description || product.what_to_expect || '').slice(0, 5000),
    image: product.image_url || product.gallery_images?.[0] || BRAND_LOGO,
    url,
    provider: {
      '@type': 'LocalBusiness',
      name: product.host?.business_name || BRAND_NAME,
      url: SITE_URL,
      logo: BRAND_LOGO,
      areaServed: cityDisplay
        ? { '@type': 'City', name: cityDisplay }
        : undefined,
    },
    areaServed: cityDisplay ? { '@type': 'City', name: cityDisplay } : undefined,
    offers: price > 0
      ? {
          '@type': 'Offer',
          price: price.toFixed(2),
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          url,
        }
      : undefined,
    aggregateRating: product.rating
      ? {
          '@type': 'AggregateRating',
          ratingValue: product.rating,
          reviewCount: product.reviews_count || 1,
        }
      : undefined,
  };
}

/* ============= PRODUCT (commerce variant — used for rentals) ============= */
export function productLD(product) {
  if (!product) return null;
  const url = typeof window !== 'undefined' ? window.location.href : SITE_URL;
  const price = Number(product.customer_price ?? product.base_price ?? product.price ?? 0);
  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.name,
    description: (product.short_description || product.description || '').slice(0, 5000),
    image: product.image_url || product.gallery_images?.[0] || BRAND_LOGO,
    brand: { '@type': 'Brand', name: BRAND_NAME },
    offers: price > 0
      ? {
          '@type': 'Offer',
          price: price.toFixed(2),
          priceCurrency: 'USD',
          availability: 'https://schema.org/InStock',
          url,
        }
      : undefined,
  };
}

/* ============= FOOD-TRUCK PRODUCT (food-truck detail page) =============
 * Emits a Product graph for a food-truck menu listing. Google's Product
 * rich-results allow per-guest pricing via Offer, AggregateRating from the
 * parent vendor, brand (the vendor), and areaServed (the city) — earning
 * eligibility for the "Find a food truck near you" panel + price chip in
 * standard organic results.
 *
 * `data` is the API response from `GET /api/public/food-trucks/{slug}`
 * with `kind === 'service_product'`. The vendor brand-page variant uses
 * `foodTruckVendorLD()` below.
 */
export function foodTruckProductLD(data, slug) {
  if (!data) return null;
  const url = `${SITE_URL}/food-trucks/${slug || data.slug || ''}`;
  const price = Number(data.base_price ?? 0);
  const cityDisplay = data.city
    ? String(data.city).replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())
    : undefined;
  const images = [data.image_url, ...(data.gallery_images || [])].filter(Boolean);
  const ld = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: data.name,
    description: (data.short_description || data.description || '').slice(0, 5000),
    image: images.length > 0 ? images : [BRAND_LOGO],
    url,
    category: 'Food truck catering',
    brand: {
      '@type': 'Brand',
      name: data.vendor?.name || BRAND_NAME,
    },
    additionalProperty: [
      data.sub_category && {
        '@type': 'PropertyValue',
        name: 'Cuisine',
        value: String(data.sub_category).replace(/[-_]/g, ' '),
      },
      data.min_guests && {
        '@type': 'PropertyValue',
        name: 'Minimum guests',
        value: data.min_guests,
      },
      data.max_guests && {
        '@type': 'PropertyValue',
        name: 'Maximum guests',
        value: data.max_guests,
      },
      cityDisplay && {
        '@type': 'PropertyValue',
        name: 'Service area',
        value: cityDisplay,
      },
    ].filter(Boolean),
  };
  if (price > 0) {
    ld.offers = {
      '@type': 'Offer',
      price: price.toFixed(2),
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: price.toFixed(2),
        priceCurrency: 'USD',
        unitText: data.pricing_unit_label || 'per guest',
        referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitText: 'guest' },
      },
      availability: 'https://schema.org/InStock',
      url,
      areaServed: cityDisplay ? { '@type': 'City', name: cityDisplay } : undefined,
      seller: data.vendor?.name
        ? { '@type': 'Organization', name: data.vendor.name }
        : undefined,
    };
    // Strip undefined offer fields
    Object.keys(ld.offers).forEach((k) => ld.offers[k] === undefined && delete ld.offers[k]);
  }
  if (data.vendor?.rating != null && data.vendor.review_count > 0) {
    ld.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: Number(data.vendor.rating),
      reviewCount: Number(data.vendor.review_count),
      bestRating: 5,
      worstRating: 1,
    };
  }
  // Strip top-level undefined / empty arrays
  if (!ld.additionalProperty?.length) delete ld.additionalProperty;
  return ld;
}

/* ============= FOOD-TRUCK VENDOR BRAND PAGE =============
 * Emits a FoodEstablishment graph for the vendor "brand" food-truck page
 * (e.g. /food-trucks/savory-food-truck). Google recognises FoodEstablishment
 * for local + carousel placement when a vendor has multiple service menus.
 */
export function foodTruckVendorLD(data, slug) {
  if (!data) return null;
  const url = `${SITE_URL}/food-trucks/${slug || data.slug || ''}`;
  const cityDisplay = data.city
    ? String(data.city).replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())
    : undefined;
  // Unique cities the brand serves (derived from menus[])
  const citiesServed = Array.from(new Set((data.menus || [])
    .map((m) => m.city)
    .filter(Boolean)));
  const ld = {
    '@context': 'https://schema.org',
    '@type': ['FoodEstablishment', 'Organization'],
    name: data.name,
    description: (data.description || '').slice(0, 5000),
    image: data.image_url || BRAND_LOGO,
    url,
    servesCuisine: Array.from(new Set((data.menus || [])
      .map((m) => m.sub_category)
      .filter(Boolean)
      .map((s) => String(s).replace(/[-_]/g, ' ')))),
    areaServed: citiesServed.length
      ? citiesServed.map((c) => ({
          '@type': 'City',
          name: String(c).replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase()),
        }))
      : (cityDisplay ? [{ '@type': 'City', name: cityDisplay }] : undefined),
  };
  if (data.price_from != null) {
    // priceRange uses $/$$/$$$/$$$$ — derive a rough indicator.
    const p = Number(data.price_from);
    ld.priceRange = p < 20 ? '$' : p < 40 ? '$$' : p < 75 ? '$$$' : '$$$$';
  }
  if (data.rating != null && data.review_count > 0) {
    ld.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: Number(data.rating),
      reviewCount: Number(data.review_count),
      bestRating: 5,
      worstRating: 1,
    };
  }
  if (!ld.servesCuisine?.length) delete ld.servesCuisine;
  return ld;
}

/* ============= VENUE (venue detail page) ============= */
export function venueLD(venue) {
  if (!venue) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'EventVenue',
    name: venue.name,
    description: (venue.description || '').slice(0, 5000),
    image: venue.hero_image_url || venue.image_url || venue.gallery?.[0] || BRAND_LOGO,
    url: typeof window !== 'undefined' ? window.location.href : SITE_URL,
    address: {
      '@type': 'PostalAddress',
      streetAddress: venue.address || '',
      addressLocality: venue.city || '',
      addressRegion: venue.state || '',
      postalCode: venue.zip || venue.postal_code || '',
      addressCountry: 'US',
    },
    geo: venue.lat && venue.lng
      ? { '@type': 'GeoCoordinates', latitude: venue.lat, longitude: venue.lng }
      : undefined,
    maximumAttendeeCapacity: venue.capacity || venue.max_guests || undefined,
    amenityFeature: (venue.amenities || []).map((a) => ({
      '@type': 'LocationFeatureSpecification',
      name: a,
      value: true,
    })),
  };
}

/* ============= ITEM LIST (category listing pages) ============= */
export function itemListLD(items, listName) {
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    numberOfItems: items?.length || 0,
    itemListElement: (items || []).slice(0, 50).map((it, idx) => ({
      '@type': 'ListItem',
      position: idx + 1,
      name: it.name || it.title,
      url: it.url?.startsWith('http') ? it.url : `${SITE_URL}${it.url || ''}`,
      image: it.image_url || undefined,
    })),
  };
}

/* ============= EVENT LIST (events index, tag/category pages) =============
 * Google's Events search rewards listing pages that surface FULL Event
 * objects (not just name+url stubs) so each event becomes individually
 * eligible for the Events carousel even when the visitor only sees the
 * index. Drop this onto every page that lists events.
 */
export function eventsItemListLD(events, listName, baseUrl) {
  if (!events?.length) return null;
  const base = baseUrl?.startsWith('http') ? baseUrl : `${SITE_URL}${baseUrl || ''}`;
  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: listName,
    numberOfItems: events.length,
    itemListElement: events.slice(0, 50).map((e, idx) => {
      // `_publicUrlPrefix` lets directory pages (experiences=/ex,
      // reservations=/res) emit ListItem URLs in the correct silo so
      // Google's ItemList graph stays consistent with the page URL.
      const prefix = e._publicUrlPrefix || '/events';
      const url = e.url?.startsWith('http')
        ? e.url
        : `${SITE_URL}${e.url || `${prefix}/${e.city || ''}/${e.slug || ''}`}`;
      const ev = {
        '@type': resolveEventType(e),
        '@id': url,
        name: e.name || e.title,
        url,
        startDate: e.start_datetime || isoWithTimezone(e.event_date, e.event_time || e.start_time, e.city),
        endDate: e.end_datetime || (e.end_time ? isoWithTimezone(e.event_date, e.end_time, e.city) : undefined),
        eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
        eventStatus: 'https://schema.org/EventScheduled',
        image: buildImageArray(e),
        description: (e.tagline || e.short_description || e.description || '').slice(0, 500),
        organizer: { '@type': 'Organization', name: BRAND_NAME, url: SITE_URL },
      };
      if (e.venue_name || e.venue) {
        ev.location = {
          '@type': 'Place',
          name: e.venue_name || (typeof e.venue === 'string' ? e.venue : e.venue?.name) || 'TBA',
          address: {
            '@type': 'PostalAddress',
            streetAddress: e.venue_address || '',
            addressLocality: e.city || (typeof e.venue === 'object' ? e.venue?.city : '') || '',
            addressRegion: e.state || (typeof e.venue === 'object' ? e.venue?.state : '') || '',
            postalCode: e.zip || (typeof e.venue === 'object' ? e.venue?.zip : '') || '',
            addressCountry: 'US',
          },
        };
      }
      const performers = e.performers || (e.performer ? [e.performer] : []);
      if (performers.length > 0) {
        ev.performer = performers.map((p) => (
          typeof p === 'object' && p !== null
            ? { '@type': p.type || 'PerformingGroup', name: p.name }
            : { '@type': 'PerformingGroup', name: String(p) }
        ));
      }
      const offers = buildOffersArray(e, url);
      if (offers && offers.length > 0) {
        ev.offers = offers.length === 1 ? offers[0] : offers;
      }
      return {
        '@type': 'ListItem',
        position: idx + 1,
        item: ev,
      };
    }),
    url: base,
  };
}

/* ============= COLLECTION PAGE (catalog) ============= */
export function collectionPageLD(name, description) {
  return {
    '@context': 'https://schema.org',
    '@type': 'CollectionPage',
    name,
    description,
    url: typeof window !== 'undefined' ? window.location.href : SITE_URL,
    isPartOf: { '@id': `${SITE_URL}#website` },
  };
}

/* ============= BLOG POST ============= */
export function blogPostingLD(post) {
  if (!post) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: (post.excerpt || post.subtitle || '').slice(0, 500),
    image: post.cover_image_url || post.image_url || BRAND_LOGO,
    datePublished: post.published_at || post.created_at,
    dateModified: post.updated_at || post.published_at || post.created_at,
    author: {
      '@type': post.author_type === 'organization' ? 'Organization' : 'Person',
      name: post.author_name || BRAND_NAME,
    },
    publisher: {
      '@type': 'Organization',
      name: BRAND_NAME,
      logo: { '@type': 'ImageObject', url: BRAND_LOGO },
    },
    mainEntityOfPage: typeof window !== 'undefined' ? window.location.href : SITE_URL,
  };
}

/* ============= FAQ PAGE ============= */
export function faqLD(faqs) {
  if (!faqs?.length) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.question || f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.answer || f.a },
    })),
  };
}


/* ============= EXPERIENCE (Google Product + TouristAttraction graph) =============
 * Tours / tastings / classes (`kind=experience`) want to appear in BOTH:
 *   1. Google Shopping / Product rich results — earns the "price chip"
 *      and Top Stories style placement on mobile.
 *   2. Google Travel / Things to do panels — earns inclusion in the
 *      "Top experiences in {city}" carousel.
 *
 * We emit a combined graph: a Product node (with AggregateOffer pulling
 * `min_price_cents` / `max_price_cents` from the open slots) PLUS a
 * TouristAttraction node so Google can match both intents.
 *
 * Note: Google requires `image` (>=1), `name`, `description`, `offers`,
 * and at least one of `aggregateRating` OR `review` for Product rich
 * results. We omit aggregateRating until reviews exist (Google warns on
 * empty/zero values), preserving eligibility for future enhancement.
 */
export function experienceProductLD(event) {
  if (!event) return null;
  const url = typeof window !== 'undefined' ? window.location.href : SITE_URL;
  const images = [event.image_url, ...(event.gallery_images || [])].filter(Boolean);
  const description = (event.short_description || event.description || event.tagline || '').slice(0, 5000);
  const minCents = Number(event.min_price_cents ?? 0);
  const maxCents = Number(event.max_price_cents ?? minCents);
  const openSlots = Number(event.open_slot_count ?? 0);

  // aggregateRating + review[] — Google requires ≥1 review for the gold
  // star chip. We pull from `event._reviews` (populated by EventDetailPage
  // when it fetches /api/events/{event_id}/reviews/aggregate + /reviews).
  const rev = event._reviews || {};
  const aggregateRating = (rev.count && rev.count > 0 && rev.rating)
    ? {
        '@type': 'AggregateRating',
        ratingValue: Number(rev.rating).toFixed(2),
        reviewCount: rev.count,
        bestRating: 5,
        worstRating: 1,
      }
    : null;
  const reviewArray = (rev.items || []).slice(0, 5).map(r => ({
    '@type': 'Review',
    reviewRating: {
      '@type': 'Rating',
      ratingValue: r.rating,
      bestRating: 5,
      worstRating: 1,
    },
    author: { '@type': 'Person', name: r.reviewer_name || 'Guest' },
    datePublished: r.published_at,
    ...(r.title ? { name: r.title } : {}),
    ...(r.body ? { reviewBody: r.body } : {}),
  }));

  const offers = minCents > 0
    ? (maxCents > minCents
      ? {
          '@type': 'AggregateOffer',
          priceCurrency: 'USD',
          lowPrice: (minCents / 100).toFixed(2),
          highPrice: (maxCents / 100).toFixed(2),
          offerCount: openSlots || 1,
          availability: openSlots > 0
            ? 'https://schema.org/InStock'
            : 'https://schema.org/PreOrder',
          url,
        }
      : {
          '@type': 'Offer',
          price: (minCents / 100).toFixed(2),
          priceCurrency: 'USD',
          availability: openSlots > 0
            ? 'https://schema.org/InStock'
            : 'https://schema.org/PreOrder',
          url,
        })
    : undefined;

  const cityDisplay = event.city
    ? String(event.city).replace(/[-_]/g, ' ').replace(/\b\w/g, (m) => m.toUpperCase())
    : undefined;

  const graph = [
    {
      '@type': 'Product',
      '@id': `${url}#product`,
      name: event.name,
      description,
      image: images.length > 0 ? images : [BRAND_LOGO],
      url,
      brand: { '@type': 'Brand', name: BRAND_NAME },
      category: 'Experiences and tours',
      ...(offers ? { offers } : {}),
      ...(aggregateRating ? { aggregateRating } : {}),
      ...(reviewArray.length ? { review: reviewArray } : {}),
      ...(event.duration_minutes
        ? {
            additionalProperty: [
              { '@type': 'PropertyValue', name: 'Duration (minutes)', value: event.duration_minutes },
              ...(event.min_age ? [{ '@type': 'PropertyValue', name: 'Minimum age', value: event.min_age }] : []),
              ...(event.max_group_size ? [{ '@type': 'PropertyValue', name: 'Max group size', value: event.max_group_size }] : []),
              ...((event.languages || []).length
                ? [{ '@type': 'PropertyValue', name: 'Languages', value: (event.languages || []).join(', ') }]
                : []),
            ],
          }
        : {}),
    },
    {
      '@type': 'TouristAttraction',
      '@id': `${url}#attraction`,
      name: event.name,
      description,
      image: images.length > 0 ? images : [BRAND_LOGO],
      url,
      ...(event.venue_address
        ? {
            address: {
              '@type': 'PostalAddress',
              streetAddress: event.venue_address,
              addressLocality: event.venue_city || cityDisplay,
              addressRegion: event.venue_state,
              postalCode: event.venue_zip,
              addressCountry: 'US',
            },
          }
        : {}),
    },
  ];

  return {
    '@context': 'https://schema.org',
    '@graph': graph,
  };
}

/* ============= RESERVATION (Restaurant / FoodEstablishment) =============
 * Table reservations get a `Restaurant` graph + `Menu` shortcut so
 * Google can show the venue in "restaurants near me" + "book a table"
 * intents. The actual reservation event uses
 * `FoodEstablishmentReservation` once confirmed (handled at booking).
 */
export function reservationVenueLD(event) {
  if (!event) return null;
  const url = typeof window !== 'undefined' ? window.location.href : SITE_URL;
  const images = [event.image_url, ...(event.gallery_images || [])].filter(Boolean);
  const description = (event.short_description || event.description || event.tagline || '').slice(0, 5000);
  const partyMin = Number(event.party_size_min ?? 1);
  const partyMax = Number(event.party_size_max ?? 8);

  const rev = event._reviews || {};
  const aggregateRating = (rev.count && rev.count > 0 && rev.rating)
    ? {
        '@type': 'AggregateRating',
        ratingValue: Number(rev.rating).toFixed(2),
        reviewCount: rev.count,
        bestRating: 5,
        worstRating: 1,
      }
    : null;
  const reviewArray = (rev.items || []).slice(0, 5).map(r => ({
    '@type': 'Review',
    reviewRating: { '@type': 'Rating', ratingValue: r.rating, bestRating: 5, worstRating: 1 },
    author: { '@type': 'Person', name: r.reviewer_name || 'Guest' },
    datePublished: r.published_at,
    ...(r.title ? { name: r.title } : {}),
    ...(r.body ? { reviewBody: r.body } : {}),
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'Restaurant',
    name: event.venue_name || event.name,
    description,
    image: images.length > 0 ? images : [BRAND_LOGO],
    url,
    servesCuisine: event.cuisine || undefined,
    priceRange: event.price_range || '$$',
    acceptsReservations: 'True',
    ...(aggregateRating ? { aggregateRating } : {}),
    ...(reviewArray.length ? { review: reviewArray } : {}),
    ...(event.venue_address
      ? {
          address: {
            '@type': 'PostalAddress',
            streetAddress: event.venue_address,
            addressLocality: event.venue_city,
            addressRegion: event.venue_state,
            postalCode: event.venue_zip,
            addressCountry: 'US',
          },
        }
      : {}),
    potentialAction: {
      '@type': 'ReserveAction',
      target: {
        '@type': 'EntryPoint',
        urlTemplate: url,
        actionPlatform: [
          'http://schema.org/DesktopWebPlatform',
          'http://schema.org/MobileWebPlatform',
        ],
      },
      result: {
        '@type': 'FoodEstablishmentReservation',
        partySize: { '@type': 'QuantitativeValue', minValue: partyMin, maxValue: partyMax },
        name: `Reserve a table at ${event.venue_name || event.name}`,
      },
    },
  };
}
